
/**
 * Tenants Page - Data Table Implementation
 * Refactored for high data density and better information hierarchy (Stripe-like).
 */

import { useState, useMemo } from 'react'
import {
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

import { useTenants } from '@/hooks' // Assuming this exports useTenants
import type { Tenant } from '@/lib/types'
import { usePagination } from '@/hooks/use-pagination'
import { TenantUsageModal } from './TenantUsageModal'
import { TenantsStats } from './TenantsStats'
import { TenantsFilters } from './TenantsFilters'
import { TenantsList } from './TenantsList'
import type { FilterBilling, FilterPlan } from '../types'

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
export function TenantsPage() {
  const { page, limit, setPage, setLimit } = usePagination()
  const { data: response, isLoading, refetch } = useTenants({ page, limit })

  // Safely extract data and meta
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tenants = Array.isArray(response) ? response : ((response as any)?.data || [])
  // @ts-ignore - Meta might not exist on initial load or error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  const meta = (response as any)?.meta || { page: 1, limit: 20, total: 0 }

  // UI State
  const [searchQuery, setSearchQuery] = useState('')
  const [filterPlan, setFilterPlan] = useState<FilterPlan>('all')
  const [filterBilling, setFilterBilling] = useState<FilterBilling>('all')
  const [showUsageModal, setShowUsageModal] = useState(false)
  const [usageTenant, setUsageTenant] = useState<Tenant | null>(null)

  // Derive itemsPerPage from limit for consistency
  const itemsPerPage = limit

  // Filtering (Client-side filtering on current page - ideal world: server side filtering)
  const filteredTenants = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return tenants.filter((tenant: any) => {
      const matchesSearch = searchQuery === '' ||
        tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (tenant.domain && tenant.domain.toLowerCase().includes(searchQuery.toLowerCase()))

      const matchesPlan = filterPlan === 'all' ||
        ((tenant as any).tenantPlan && (tenant as any).tenantPlan.planTier === filterPlan) ||
        ((tenant as any).license && (tenant as any).license.plan === filterPlan)

      const matchesBilling = filterBilling === 'all' ||
        ((tenant as any).tenantPlan?.billingStatus === filterBilling)

      return matchesSearch && matchesPlan && matchesBilling
    })
  }, [tenants, searchQuery, filterPlan, filterBilling, itemsPerPage])

  const handleViewUsage = (tenant: Tenant) => {
    setUsageTenant(tenant)
    setShowUsageModal(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Tenants</h2>
          <p className="text-sm text-muted-foreground">Monitor client instances and resource consumption.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} className="h-9">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <TenantsStats tenants={tenants} />

      {/* Filters Bar */}
      <TenantsFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterPlan={filterPlan}
        setFilterPlan={setFilterPlan}
        filterBilling={filterBilling}
        setFilterBilling={setFilterBilling}
      />

      {/* Data Table */}
      <TenantsList
        tenants={filteredTenants}
        isLoading={isLoading}
        currentPage={page}
        totalPages={Math.ceil(filteredTenants.length / itemsPerPage)}
        itemsPerPage={itemsPerPage}
        totalCount={filteredTenants.length}
        onPageChange={setPage}
        onItemsPerPageChange={setLimit}
        onViewUsage={handleViewUsage}
      />

      {showUsageModal && usageTenant && (
        <TenantUsageModal
          tenant={usageTenant}
          onClose={() => {
            setShowUsageModal(false)
            setUsageTenant(null)
          }}
        />
      )}
    </div>
  )
}
